<template>
  <div class="admin-dashboard-page">
    <span v-if="roleAdmin !== 'government' && roleAdmin !== 'markter'">
      <template v-if="roleAdmin !== 'engineer-allocation' && roleAdmin !== 'engineer'">
        <!-- Dashboard Header -->
        <div class="dashboard-header">
          <div class="header-content">
            <div class="header-left">
              <div class="header-icon-wrapper">
                <feather-icon
                  icon="PieChartIcon"
                  size="28"
                />
              </div>
              <div class="header-text">
                <h1 class="page-title">
                  Analytics Dashboard
                </h1>
                <p class="page-subtitle">
                  Branch performance & engineer analytics
                </p>
              </div>
            </div>
            <div class="header-right">
              <feather-icon
                icon="BarChart2Icon"
                size="24"
                class="header-accent-icon"
              />
            </div>
          </div>
        </div>

        <div class="dashboard-request-search">
          <div class="dashboard-request-search__card">
            <div class="dashboard-request-search__header">
              <feather-icon
                icon="SearchIcon"
                size="16"
                class="mr-50"
              />
              <span>Find request</span>
            </div>

            <div class="dashboard-request-search__body">
              <div class="dashboard-request-search__pill">
                <div class="dashboard-request-search__prefix">
                  <feather-icon
                    icon="FilterIcon"
                    size="13"
                  />
                </div>
                <span class="dashboard-request-search__label">Request Code</span>
                <span class="dashboard-request-search__sep">contains</span>
                <b-form-input
                  v-model="requestCodeQuery"
                  class="dashboard-request-search__input"
                  placeholder="e.g. 203040"
                  :disabled="requestSearchLoading"
                  @keydown.enter.prevent="searchRequestByCode"
                />
                <button
                  type="button"
                  class="dashboard-request-search__btn"
                  :disabled="requestSearchLoading || !requestCodeQuery.trim()"
                  @click="searchRequestByCode"
                >
                  <b-spinner
                    v-if="requestSearchLoading"
                    small
                    variant="light"
                  />
                  <template v-else>
                    <feather-icon
                      icon="SearchIcon"
                      size="14"
                      class="mr-25"
                    />
                    Search
                  </template>
                </button>
              </div>

              <p
                v-if="requestSearchError"
                class="dashboard-request-search__error mb-0"
              >
                {{ requestSearchError }}
              </p>

              <div
                v-if="requestSearchResults.length"
                class="dashboard-request-search__results"
              >
                <div class="dashboard-request-search__results-head">
                  <span>{{ requestSearchResults.length }} result(s)</span>
                  <button
                    type="button"
                    class="dashboard-request-search__link"
                    @click="openInRequestsPage"
                  >
                    Open in Requests
                  </button>
                </div>
                <ul class="dashboard-request-search__list">
                  <li
                    v-for="item in requestSearchResults"
                    :key="item.id || item.request_code"
                    class="dashboard-request-search__item"
                  >
                    <div>
                      <strong>{{ item.request_code || '—' }}</strong>
                      <span class="dashboard-request-search__meta">
                        {{ item.facility_name || item.client_name || item.requester || '—' }}
                      </span>
                    </div>
                    <span class="dashboard-request-search__status">
                      {{ item.internal_status || item.status || '' }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="dashboard-layout">
          <div class="branches-sidebar">
            <OurBranches
              ref="branches"
              class="branches-fill"
              :role-admin="roleAdmin"
              @dataChartToTable="dataChartEmit"
              @loadingChart="getLoading"
              @loadingDataUser="getLoadingDataUser"
              @userData="getUserData"
            />
          </div>
          <div class="chart-content">
            <div
              v-if="isLoading"
              class="loading-wrapper chart-fill"
            >
              <div class="loading-spinner">
                <b-spinner
                  variant="primary"
                  type="grow"
                  label="Spinning"
                />
              </div>
              <span class="loading-text">Loading analytics...</span>
            </div>
            <IndexChartBar
              v-else
              class="chart-fill"
              :data-chart="dataChart"
              :user-data="userData"
              :user-loading="isUserLoading"
              :chart-loading="isChartLoading"
              @need-user-data="ensureUserDataLoaded"
            />
          </div>
        </div>
      </template>
    </span>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import OurBranches from './our-branches/OurBranches.vue'
import IndexChartBar from './components/chart-bar-engineer/IndexChartBar.vue'

export default {
  components: {
    OurBranches,
    IndexChartBar,
  },
  data() {
    return {
      dataChart: {},
      userData: [],
      isLoading: false,
      isUserLoading: false,
      isChartLoading: false,
      roleAdmin: localStorage.getItem('user_role'),
      requestCodeQuery: '',
      requestSearchLoading: false,
      requestSearchError: '',
      requestSearchResults: [],
    }
  },
  methods: {
    dataChartEmit(getChartData) {
      this.dataChart = getChartData
    },
    getLoading(getLoading) {
      this.isChartLoading = getLoading
    },
    getLoadingDataUser(getLoading) {
      this.isUserLoading = getLoading
    },
    getUserData(data) {
      if (Array.isArray(data)) {
        this.userData = data
      }
    },
    ensureUserDataLoaded(userId) {
      if (this.$refs.branches && typeof this.$refs.branches.getUserData === 'function') {
        this.$refs.branches.getUserData(userId)
      }
    },
    normalizeRequestList(payloadData) {
      if (!payloadData) return []
      if (Array.isArray(payloadData)) return payloadData
      if (Array.isArray(payloadData.requests)) return payloadData.requests
      if (Array.isArray(payloadData.data)) return payloadData.data
      const nested = Object.values(payloadData).find(Array.isArray)
      return nested || []
    },
    async searchRequestByCode() {
      const requestCode = this.requestCodeQuery.trim()
      if (!requestCode || this.requestSearchLoading) return

      this.requestSearchLoading = true
      this.requestSearchError = ''
      this.requestSearchResults = []

      try {
        const response = await axios.get('requests', {
          params: {
            skip: 0,
            request_code: requestCode,
          },
        })

        if (response.status === 200) {
          const payload = decryptData(response.data.payload)
          const list = this.normalizeRequestList(payload?.data ?? payload)
          this.requestSearchResults = list

          if (!list.length) {
            this.requestSearchError = `No requests found for code "${requestCode}".`
          }
        }
      } catch (error) {
        this.requestSearchError = 'Failed to search requests.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Request search failed',
            icon: 'BellIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.requestSearchLoading = false
      }
    },
    openInRequestsPage() {
      const requestCode = this.requestCodeQuery.trim()
      if (!requestCode) return
      this.$router.push({
        name: 'requests',
        query: { request_code: requestCode },
      })
    },
  },
}
</script>

<style lang="scss">
.admin-dashboard-page {
  padding: 0.5rem 0;
}

.dashboard-header {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  border-radius: 12px;
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(115, 103, 240, 0.35);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  backdrop-filter: blur(4px);
}

.header-text {
  .page-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.25rem 0;
    letter-spacing: -0.02em;
  }

  .page-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    margin: 0;
    font-weight: 500;
  }
}

.header-right .header-accent-icon {
  color: rgba(255, 255, 255, 0.6);
}

.dashboard-request-search {
  margin-bottom: 1.5rem;
}

.dashboard-request-search__card {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  overflow: hidden;
}

.dashboard-request-search__header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #ebe9f1;
  font-size: 0.85rem;
  font-weight: 700;
  color: #5e5873;
  background: #f8f8f8;
}

.dashboard-request-search__body {
  padding: 1rem;
}

.dashboard-request-search__pill {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.65rem;
  border-radius: 999px;
  background: radial-gradient(circle at top left, #eef2ff 0, #f9fafb 45%, #ecfeff 100%);
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 10px rgba(148, 163, 184, 0.2);
}

.dashboard-request-search__prefix {
  display: inline-flex;
  align-items: center;
  color: #7367f0;
  padding-right: 0.4rem;
  border-right: 1px solid #e5e7eb;
}

.dashboard-request-search__label {
  font-size: 0.78rem;
  font-weight: 800;
  color: #4f46e5;
  white-space: nowrap;
}

.dashboard-request-search__sep {
  font-size: 0.72rem;
  color: #6b7280;
  white-space: nowrap;
}

.dashboard-request-search__input {
  flex: 1 1 160px;
  min-width: 140px;
  font-size: 0.85rem;
}

.dashboard-request-search__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  min-width: 96px;
  padding: 0.4rem 0.9rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.dashboard-request-search__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.dashboard-request-search__error {
  margin-top: 0.75rem;
  color: #ea5455;
  font-size: 0.82rem;
}

.dashboard-request-search__results {
  margin-top: 0.9rem;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  overflow: hidden;
}

.dashboard-request-search__results-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.85rem;
  background: #f8f8f8;
  font-size: 0.78rem;
  font-weight: 700;
  color: #6e6b7b;
}

.dashboard-request-search__link {
  border: none;
  background: transparent;
  color: #7367f0;
  font-weight: 700;
  font-size: 0.78rem;
  cursor: pointer;
  padding: 0;
}

.dashboard-request-search__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
}

.dashboard-request-search__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem;
  border-top: 1px solid #f0f0f0;
}

.dashboard-request-search__meta {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: #b9b9c3;
}

.dashboard-request-search__status {
  font-size: 0.72rem;
  font-weight: 700;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .dashboard-request-search__pill {
    border-radius: 12px;
  }

  .dashboard-request-search__btn {
    width: 100%;
  }
}

.dashboard-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1.5rem;
  min-height: calc(80vh - 2rem);

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
}

.branches-sidebar {
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.chart-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.branches-fill,
.chart-fill {
  flex: 1;
  min-height: 0;
}

.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
}

.loading-spinner {
  margin-bottom: 1rem;
}

.loading-text {
  font-size: 0.95rem;
  color: #b9b9c3;
  font-weight: 500;
}
</style>
